import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React from "react";
import { TBankAccountFormValues } from "../../../../models/Onboarding/onboarding";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import { showNotificationAction } from "../../../../store/application/slice";
import { setBankAccErrorMsgAction } from "../../../../store/onboarding/slice";
import { saveBankAccountThunk } from "../../../../store/onboarding/thunk";
import { createValidationSchema } from "../../../../utils/createValidationSchema";
import CustomButton from "../../../UI/Buttons/CustomButton";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import TextInput from "../../../UI/Inputs/TextInput";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";

interface EditBankAccountProps {
  handleClose: (value: boolean) => void;
  type?: string
}

const EditBankAccount: React.FC<EditBankAccountProps> = ({ handleClose, type }) => {
  const dispatch = useDispatch();

  const inProgress = useSelector((state) => state.onboarding.inProgress);
  const bankAccountDetails = useSelector(
    (state) => state.onboarding.bankAccount
  );
  const bankAccErrors = useSelector(
    (state) => state.onboarding.bankAccErrors
  );

  /** Submit the form for bank-account-details */
  const { handleSubmit, values, errors, touched, handleChange } = useFormik({
    validationSchema: createValidationSchema("bankAccount"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      account_name: bankAccountDetails !== null ? bankAccountDetails.account_name : "",
      account_bsb: bankAccountDetails !== null ? bankAccountDetails.account_bsb : "",
      account_number: bankAccountDetails !== null ? bankAccountDetails.account_number : "",
    },
    onSubmit: async (values: TBankAccountFormValues) => {
      dispatch(setBankAccErrorMsgAction({}))
      const payload = { ...values };
      try {
        const result = await dispatch(saveBankAccountThunk(payload));
        if (isFulfilled(result)) {
          dispatch(showNotificationAction("Saved"));
          handleClose(false);
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  return (
    <div className="d-flex flex-column gap-15 fw-400 text-black">
      <div className="fs-22">{type ? type : "Edit"} Bank Account</div>
      <div className="fs-16 fw-400 text-black">
        Please provide your bank account details where you want to receive
        payments.
      </div>
      <div className="d-flex flex-column gap-20 max-w-450px">
        <div className="flex-fill">
          <TextInput
            value={values.account_name}
            field={"account_name"}
            label={"Account Name"}
            onChange={handleChange}
            touched={touched.account_name! as boolean}
            error={errors.account_name as string}
            placeholder={" " + ""}
          />
          {!errors.account_name && bankAccErrors?.account_name?.map((msg, index) => (
            <ErrorMessage className="flex-fill" key={`bsb-${index}`} message={msg} />
          ))}
        </div>
        <div className="d-flex flex-column flex-lg-row gap-10">
          <div className="w-180px min-w-180px mobile-w-100">
            <TextInput
              value={values.account_bsb}
              field={"account_bsb"}
              label={"BSB"}
              onChange={handleChange}
              touched={touched.account_bsb! as boolean}
              error={errors.account_bsb as string}
              placeholder={" " + ""}
            />
            {!errors.account_bsb && bankAccErrors?.account_bsb?.map((msg, index) => (
              <ErrorMessage className="w-180px" key={`bsb-${index}`} message={msg} />
            ))}
          </div>
          <div className="flex-fill">
            <TextInput
              value={values.account_number}
              field={"account_number"}
              label={"Account Number"}
              onChange={handleChange}
              touched={touched.account_number! as boolean}
              error={errors.account_number as string}
              placeholder={" " + ""}
            />
            {!errors.account_number && bankAccErrors?.account_number?.map((msg, index) => (
              <ErrorMessage className="flex-fill" key={`acc-${index}`} message={msg} />
            ))}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          onClick={handleSubmit}
          isLoading={inProgress}
          contentClass="d-flex"
        >
          <RoundedTick color={inProgress ? "#aaaaaa" : "black"} />
          Save
        </CustomButton>
      </div>
    </div>
  );
};

export default EditBankAccount;
